import { ButtonLink, ItemTitle } from '@sleepstation/components'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import {
  DIRECTION,
  LAYOUT_ALIGN,
  LAYOUT_SPACING,
  SIZE,
} from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import {
  backgroundColor,
  height,
  marginV,
  minWidth,
  width,
} from '@sleepstation/style-api-helpers'
import { isRunningInBrowser, yearsInSeconds } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { isNotNil } from 'ramda-adjunct'
import React, { useState } from 'react'
import { Cookies, withCookies } from 'react-cookie'
import styled from 'styled-components'
import { getVersionedCookieName } from '../../../../helpers/cookies'

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(BreakingLayout).attrs({
  spacing: LAYOUT_SPACING.LARGE,
  direction: [DIRECTION.VERTICAL, DIRECTION.HORIZONTAL],
  align: [LAYOUT_ALIGN.LEFT, LAYOUT_ALIGN.CENTER],
})`
  position: relative;
  ${marginV([0, 0, 0, -0.25])}
`

const CloseWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`

const Primary = styled.div`
  ${minWidth([3, 5, 4.5])}
  ${width([3, 5, 4.5])}
`

const Secondary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.MEDIUM,
})``

const Body = styled.div``

const Outer = styled.div`
  &::before {
    content: '';
    position: absolute;
    ${height(0.25)}
    top: 0;
    left: 0;
    right: 0;
    ${backgroundColor(`negative`)}
  }
`

const Inner = styled.div`
  position: relative;
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const AnnouncementBanner = ({ cookies, domain, cookieDefinition }) => {
  const isLive = isRunningInBrowser()
  const { title, body, name, version } = cookieDefinition
  const versionedCookieName = getVersionedCookieName(name, version)
  const cookie = cookies.get(versionedCookieName)
  const hasCookie = isNotNil(cookie)
  const bodyHtmlAst = parseToAst(body)

  const [isHidden, setIsHidden] = useState(hasCookie)

  const handleCloseClick = () => {
    cookies.set(versionedCookieName, true, {
      path: `/`,
      maxAge: yearsInSeconds(1),
      secure: true,
      domain,
    })

    setIsHidden(true)
  }

  const shouldRender = !isHidden && isLive

  return (
    shouldRender && (
      <Outer>
        <Inner>
          <Layout data-cy="announcement-banner">
            <Primary>
              {/* Insert image here, e.g. <SVG svg={Covid19IllustrationSvg} /> */}
            </Primary>
            <Secondary>
              <ItemTitle>{title}</ItemTitle>
              <Body>
                <CopyRenderer htmlAst={bodyHtmlAst} />
              </Body>
            </Secondary>
          </Layout>
          <CloseWrapper>
            <ButtonLink
              data-cy="announcement-banner-close-button"
              icon="close"
              size={SIZE.X_LARGE}
              onClick={handleCloseClick}
            />
          </CloseWrapper>
        </Inner>
      </Outer>
    )
  )
}

AnnouncementBanner.propTypes = {
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  cookieDefinition: PropTypes.object.isRequired,
  domain: PropTypes.string.isRequired,
}

export default withCookies(AnnouncementBanner)
